import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Hash, Plus, Search, Lock, Globe, Archive, RefreshCw,
  ChevronDown, ChevronUp, X, MessageSquare, ListTodo, Menu, UserPlus
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { MessageItem } from '../components/slate/MessageItem';
import { MessageComposer } from '../components/slate/MessageComposer';
import { TaskPanel } from '../components/slate/TaskPanel';
import { fmtTime } from '../components/slate/slateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Search Modal ───────────────────────────────────────────────────────────────
function SearchModal({ projectId, token, channels, onClose, onJumpTo }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef();
  const headers = { Authorization: `Bearer ${token}` };

  const search = async (query) => {
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/slate/search?q=${encodeURIComponent(query)}`, { headers });
      setResults(res.data.messages || []);
    } catch (err) { console.error('Slate search error:', err); } finally { setLoading(false); }
  };

  const chMap = Object.fromEntries((channels || []).map(c => [c.id, c.name]));

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center pt-20 z-50" onClick={onClose}>
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl w-full max-w-lg shadow-2xl mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#2a2a2a]">
          <Search className="h-4 w-4 text-[#a0a0a0]" />
          <input autoFocus className="flex-1 bg-transparent text-[#f0f0f0] text-sm outline-none placeholder-[#555]"
            placeholder="Search messages..." value={q}
            onChange={e => {
              setQ(e.target.value);
              clearTimeout(timerRef.current);
              timerRef.current = setTimeout(() => search(e.target.value), 300);
            }}
            data-testid="slate-search-input" />
          <button onClick={onClose} className="text-[#555] hover:text-[#f0f0f0]"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading && <div className="text-center py-4 text-[#555] text-sm">Searching...</div>}
          {!loading && results.length === 0 && q && <div className="text-center py-4 text-[#555] text-sm">No results</div>}
          {results.map(m => (
            <button key={m.id} onClick={() => { onJumpTo(m.channel_id); onClose(); }}
              className="w-full text-left px-4 py-3 hover:bg-[#2a2a2a] transition-colors border-b border-[#111]">
              <div className="flex items-center gap-2 mb-0.5">
                <Hash className="h-3 w-3 text-[#e8c547]" />
                <span className="text-xs text-[#e8c547]">{chMap[m.channel_id] || 'unknown'}</span>
                <span className="text-xs text-[#444]">{fmtTime(m.created_at)}</span>
              </div>
              <div className="text-sm text-[#a0a0a0] font-medium">{m.user_name}</div>
              <div className="text-xs text-[#666] truncate">{m.content}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Create Channel ─────────────────────────────────────────────────────────────
function CreateChannelModal({ projectId, token, projectUsers, onCreated, onClose }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('public');
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/slate/channels`,
        { name, type, members }, { headers });
      onCreated(res.data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-5 w-80 shadow-2xl mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="font-['DM_Serif_Display'] text-lg text-[#f0f0f0] mb-4">New Channel</h3>
        <div className="space-y-3">
          <Input value={name} onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="channel-name" className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
            data-testid="create-channel-name" />
          <div className="flex gap-2">
            {['public', 'private'].map(t => (
              <button key={t} onClick={() => setType(t)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border text-xs font-medium transition-colors ${type === t ? 'border-[#e8c547] text-[#e8c547] bg-[#e8c547]/10' : 'border-[#2a2a2a] text-[#555]'}`}>
                {t === 'public' ? <Globe className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                {t}
              </button>
            ))}
          </div>
          {type === 'private' && (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {projectUsers.map(u => (
                <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#2a2a2a] cursor-pointer">
                  <input type="checkbox" checked={members.includes(u.id)}
                    onChange={e => setMembers(prev => e.target.checked ? [...prev, u.id] : prev.filter(id => id !== u.id))}
                    className="accent-[#e8c547]" />
                  <span className="text-sm text-[#f0f0f0]">{u.full_name}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button onClick={create} disabled={saving || !name.trim()}
              className="flex-1 bg-[#e8c547] hover:bg-[#d4b340] text-black text-sm"
              data-testid="create-channel-submit">Create</Button>
            <Button variant="outline" onClick={onClose} className="border-[#2a2a2a] text-[#f0f0f0]">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── New DM Modal ───────────────────────────────────────────────────────────────
function NewDMModal({ projectId, token, projectUsers, currentUserId, onDMCreated, onClose }) {
  const [search, setSearch] = useState('');
  const [starting, setStarting] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const filtered = projectUsers.filter(u =>
    u.id !== currentUserId &&
    u.full_name.toLowerCase().includes(search.toLowerCase())
  );

  const startDM = async (targetUser) => {
    setStarting(targetUser.id);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/slate/dm`,
        { target_user_id: targetUser.id }, { headers });
      onDMCreated(res.data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setStarting(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-5 w-80 shadow-2xl mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="font-['DM_Serif_Display'] text-lg text-[#f0f0f0] mb-4">New Direct Message</h3>
        <Input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search people..."
          className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] mb-3"
          data-testid="dm-user-search"
        />
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="text-center py-4 text-[#555] text-sm">No users found</div>
          )}
          {filtered.map(u => (
            <button
              key={u.id}
              onClick={() => startDM(u)}
              disabled={starting === u.id}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#2a2a2a] transition-colors text-left"
              data-testid={`dm-user-${u.id}`}
            >
              <div className="h-8 w-8 rounded-full bg-[#e8c547]/10 border border-[#e8c547]/20 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-[#e8c547]">{u.full_name?.[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#f0f0f0] font-medium truncate">{u.full_name}</div>
                <div className="text-xs text-[#555]">{u.role?.replace(/_/g, ' ')}</div>
              </div>
              {starting === u.id && <div className="text-xs text-[#555]">...</div>}
            </button>
          ))}
        </div>
        <Button variant="outline" onClick={onClose} className="w-full mt-3 border-[#2a2a2a] text-[#f0f0f0]">Cancel</Button>
      </div>
    </div>
  );
}

// ── Channel Button ─────────────────────────────────────────────────────────────
function ChannelButton({ ch, selected, onClick, onClose, currentUserId }) {
  const [hovered, setHovered] = useState(false);
  const displayName = ch.type === 'dm'
    ? (ch.dm_users?.find(u => u.id !== currentUserId)?.name || ch.name)
    : ch.name;

  return (
    <div
      className={`flex items-center gap-1.5 px-3 py-1.5 cursor-pointer transition-colors ${selected ? 'bg-[#1a1a1a] text-[#f0f0f0]' : 'text-[#777] hover:bg-[#111] hover:text-[#d0d0d0]'}`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`channel-${ch.name}`}
    >
      {ch.type === 'dm'
        ? <span className="h-3 w-3 shrink-0 text-center text-[8px] leading-none">DM</span>
        : ch.type === 'private'
          ? <Lock className="h-3 w-3 shrink-0" />
          : <Hash className="h-3 w-3 shrink-0" />}
      <span className="text-xs flex-1 truncate">{displayName}</span>
      {ch.unread > 0 && (
        <span className="text-[9px] bg-red-500 text-white rounded-full px-1 min-w-[16px] text-center">{ch.unread}</span>
      )}
      {onClose && hovered && (
        <button onClick={e => { e.stopPropagation(); onClose(); }}
          className="h-4 w-4 flex items-center justify-center rounded text-[#555] hover:text-red-400 transition-all">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
function SlateSidebar({
  channels, selectedChannelId, setSelectedChannelId,
  currentUserId, onNewChannel, onNewDM, onClose, onSearch,
  closeChannel, reopenChannel, showBin, setShowBin
}) {
  const deptChannels = channels.filter(c => c.type === 'department' && !c.is_closed);
  const customOpen = channels.filter(c => c.type === 'public' && !c.is_closed);
  const dmChannels = channels.filter(c => c.type === 'dm' && !c.is_closed);
  const privateChannels = channels.filter(c => c.type === 'private' && !c.is_closed);
  const binChannels = channels.filter(c => c.is_closed);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-3 border-b border-[#2a2a2a]">
        <span className="font-['DM_Serif_Display'] text-base italic font-bold text-[#f0f0f0]">Slate</span>
        <div className="flex items-center gap-1">
          <button onClick={onSearch} className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-[#f0f0f0]">
            <Search className="h-3.5 w-3.5" />
          </button>
          {onClose && (
            <button onClick={onClose} className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-[#f0f0f0] md:hidden">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {/* Channels */}
        <div className="px-3 mb-1">
          <span className="text-[10px] text-[#444] font-semibold uppercase tracking-widest">Channels</span>
        </div>
        {deptChannels.map(ch => (
          <ChannelButton key={ch.id} ch={ch} selected={selectedChannelId === ch.id}
            onClick={() => { setSelectedChannelId(ch.id); onClose?.(); }}
            currentUserId={currentUserId} />
        ))}

        {(customOpen.length > 0 || privateChannels.length > 0) && (
          <>
            <div className="px-3 mb-1 mt-3">
              <span className="text-[10px] text-[#444] font-semibold uppercase tracking-widest">Topics</span>
            </div>
            {[...customOpen, ...privateChannels].map(ch => (
              <ChannelButton key={ch.id} ch={ch} selected={selectedChannelId === ch.id}
                onClick={() => { setSelectedChannelId(ch.id); onClose?.(); }}
                onClose={() => closeChannel(ch.id)} currentUserId={currentUserId} />
            ))}
          </>
        )}

        <button onClick={onNewChannel}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[#555] hover:text-[#a0a0a0] hover:bg-[#1a1a1a] transition-colors mt-2"
          data-testid="new-channel-btn">
          <Plus className="h-3.5 w-3.5" /> New Channel
        </button>

        {/* Direct Messages */}
        <div className="px-3 mb-1 mt-4">
          <span className="text-[10px] text-[#444] font-semibold uppercase tracking-widest">Direct Messages</span>
        </div>
        {dmChannels.map(ch => (
          <ChannelButton key={ch.id} ch={ch} selected={selectedChannelId === ch.id}
            onClick={() => { setSelectedChannelId(ch.id); onClose?.(); }}
            currentUserId={currentUserId} />
        ))}
        <button onClick={onNewDM}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[#555] hover:text-[#a0a0a0] hover:bg-[#1a1a1a] transition-colors mt-1"
          data-testid="new-dm-btn">
          <UserPlus className="h-3.5 w-3.5" /> New Direct Message
        </button>

        {/* BIN */}
        {binChannels.length > 0 && (
          <div className="mt-3">
            <button onClick={() => setShowBin(v => !v)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[10px] text-[#444] hover:text-[#666] uppercase tracking-widest transition-colors">
              <Archive className="h-3 w-3" /> BIN ({binChannels.length})
              {showBin ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
            </button>
            {showBin && binChannels.map(ch => (
              <div key={ch.id} className="flex items-center gap-2 px-3 py-1.5 group/bin">
                <Hash className="h-3 w-3 text-[#333] shrink-0" />
                <span className="text-xs text-[#444] flex-1 truncate">{ch.name}</span>
                <button onClick={() => reopenChannel(ch.id)} className="opacity-0 group-hover/bin:opacity-100 text-[10px] text-[#555] hover:text-[#e8c547] transition-all">
                  Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SlatePage() {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [taskPrefill, setTaskPrefill] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showNewDM, setShowNewDM] = useState(false);
  const [showBin, setShowBin] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const bottomRef = useRef();
  const pollRef = useRef();
  // Stable headers ref so functions can always get current token without stale closures
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);
  const getHeaders = () => ({ Authorization: `Bearer ${tokenRef.current}` });

  useEffect(() => { init(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedChannelId) fetchMessages(true); }, [selectedChannelId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    pollRef.current = setInterval(() => {
      if (selectedChannelId) fetchMessages(false);
      fetchNotifications();
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [selectedChannelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const init = async () => {
    setLoading(true);
    const headers = getHeaders();
    try {
      const [chRes, usersRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/slate/init`, { headers }),
        axios.get(`${API}/projects/${projectId}/slate/users`, { headers }),
      ]);
      setChannels(chRes.data);
      setProjectUsers(usersRes.data);
      const general = chRes.data.find(c => c.name === 'general') || chRes.data[0];
      if (general) setSelectedChannelId(general.id);
    } catch (err) { console.error('Slate init error:', err); toast.error('Failed to load Slate'); }
    finally { setLoading(false); }
  };

  const fetchMessages = async (scroll = false) => {
    if (!selectedChannelId) return;
    const headers = getHeaders();
    try {
      const res = await axios.get(
        `${API}/projects/${projectId}/slate/channels/${selectedChannelId}/messages`,
        { headers }
      );
      setMessages(res.data.messages || []);
      setTypingUsers(res.data.typing_users || []);
      if (scroll) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error('Fetch messages error:', err); }
  };

  const fetchNotifications = async () => {
    const headers = getHeaders();
    try {
      const res = await axios.get(`${API}/projects/${projectId}/slate/notifications`, { headers });
      const perCh = res.data.per_channel || {};
      setChannels(prev => prev.map(ch => ({ ...ch, unread: perCh[ch.id] || 0 })));
    } catch (err) { console.error('Fetch notifications error:', err); }
  };

  const handleDeleteMessage = async (msgId) => {
    const headers = getHeaders();
    try {
      await axios.delete(
        `${API}/projects/${projectId}/slate/channels/${selectedChannelId}/messages/${msgId}`,
        { headers }
      );
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (err) { console.error('Delete message error:', err); toast.error('Failed'); }
  };

  const closeChannel = async (channelId) => {
    const headers = getHeaders();
    try {
      await axios.patch(`${API}/projects/${projectId}/slate/channels/${channelId}`,
        { is_closed: true }, { headers });
      setChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, is_closed: true } : ch));
      if (selectedChannelId === channelId) {
        const next = channels.find(c => c.id !== channelId && !c.is_closed && c.name === 'general');
        setSelectedChannelId(next?.id || channels.find(c => !c.is_closed)?.id);
      }
      toast.success('Moved to BIN');
    } catch (err) { console.error('Close channel error:', err); }
  };

  const reopenChannel = async (channelId) => {
    const headers = getHeaders();
    try {
      await axios.patch(`${API}/projects/${projectId}/slate/channels/${channelId}`,
        { is_closed: false }, { headers });
      setChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, is_closed: false } : ch));
      toast.success('Channel restored');
    } catch (err) { console.error('Reopen channel error:', err); }
  };

  const handleDMCreated = (dmChannel) => {
    setChannels(prev => {
      const exists = prev.find(c => c.id === dmChannel.id);
      return exists ? prev : [...prev, dmChannel];
    });
    setSelectedChannelId(dmChannel.id);
  };

  const selectedChannel = channels.find(c => c.id === selectedChannelId);

  const groupedMessages = useMemo(() => {
    const groups = [];
    let lastDate = null;
    messages.forEach(m => {
      const d = new Date(m.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
      if (d !== lastDate) { groups.push({ type: 'divider', label: d }); lastDate = d; }
      groups.push({ type: 'msg', msg: m });
    });
    return groups;
  }, [messages]);

  const selectedChannelDisplayName = selectedChannel?.type === 'dm'
    ? (selectedChannel?.dm_users?.find(u => u.id !== user?.id)?.name || selectedChannel?.name)
    : selectedChannel?.name;

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-[#0d0d0d] text-[#a0a0a0]">Loading Slate...</div>;
  }

  return (
    <div className="flex h-screen bg-[#0d0d0d] text-[#f0f0f0] overflow-hidden">
      {/* Mobile sidebar overlay */}
      {showMobileSidebar && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setShowMobileSidebar(false)} />
      )}

      {/* Sidebar */}
      <div
        className={`bg-[#0a0a0a] border-r border-[#2a2a2a] flex flex-col
          fixed inset-y-0 left-0 z-50 w-52 transform transition-transform duration-200
          md:relative md:translate-x-0 md:flex
          ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        data-testid="slate-sidebar"
      >
        <SlateSidebar
          channels={channels}
          selectedChannelId={selectedChannelId}
          setSelectedChannelId={setSelectedChannelId}
          currentUserId={user?.id}
          onNewChannel={() => setShowCreateChannel(true)}
          onNewDM={() => setShowNewDM(true)}
          onSearch={() => setShowSearch(true)}
          onClose={() => setShowMobileSidebar(false)}
          closeChannel={closeChannel}
          reopenChannel={reopenChannel}
          showBin={showBin}
          setShowBin={setShowBin}
        />
      </div>

      {/* Main content */}
      {selectedChannel ? (
        <div className="flex flex-1 min-w-0 overflow-hidden">
          {/* Chat */}
          <div className="flex flex-col flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-[#2a2a2a] shrink-0">
              <div className="flex items-center gap-2">
                {/* Mobile hamburger */}
                <button
                  className="md:hidden h-7 w-7 flex items-center justify-center text-[#555] hover:text-[#f0f0f0] mr-1"
                  onClick={() => setShowMobileSidebar(true)}
                  data-testid="slate-mobile-menu"
                >
                  <Menu className="h-4 w-4" />
                </button>
                {selectedChannel.type === 'dm'
                  ? <MessageSquare className="h-4 w-4 text-[#4a9eff]" />
                  : selectedChannel.type === 'private'
                    ? <Lock className="h-4 w-4 text-[#555]" />
                    : <Hash className="h-4 w-4 text-[#e8c547]" />}
                <span className="font-semibold text-[#f0f0f0] text-sm sm:text-base truncate">{selectedChannelDisplayName}</span>
                {selectedChannel.type === 'department' && <Badge className="bg-[#e8c547]/10 text-[#e8c547] border-0 text-xs hidden sm:inline-flex">dept</Badge>}
                {selectedChannel.is_crosspost && <Badge className="bg-blue-500/10 text-blue-400 border-0 text-xs">cross-posted</Badge>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setShowSearch(true)} className="h-7 w-7 rounded flex items-center justify-center text-[#555] hover:text-[#f0f0f0]" data-testid="slate-search-btn">
                  <Search className="h-3.5 w-3.5" />
                </button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#555] hover:text-[#f0f0f0]"
                  onClick={() => fetchMessages(true)} data-testid="slate-refresh-btn">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
                {/* Mobile: Toggle task panel */}
                <button
                  className="md:hidden h-7 w-7 flex items-center justify-center text-[#555] hover:text-[#e8c547]"
                  onClick={() => setShowTasks(v => !v)}
                  data-testid="slate-toggle-tasks"
                >
                  <ListTodo className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-2" data-testid="slate-messages-feed">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  {selectedChannel.type === 'dm'
                    ? <MessageSquare className="h-10 w-10 text-[#2a2a2a] mb-3" />
                    : <Hash className="h-10 w-10 text-[#2a2a2a] mb-3" />}
                  <p className="text-[#555] text-sm">
                    {selectedChannel.type === 'dm'
                      ? `Start a conversation with ${selectedChannelDisplayName}`
                      : `Start the conversation in #${selectedChannelDisplayName}`}
                  </p>
                </div>
              ) : (
                groupedMessages.map((item, i) => {
                  if (item.type === 'divider') {
                    return (
                      <div key={`d-${i}`} className="flex items-center gap-3 px-4 py-2">
                        <div className="flex-1 h-px bg-[#2a2a2a]" />
                        <span className="text-[10px] text-[#444] font-medium">{item.label}</span>
                        <div className="flex-1 h-px bg-[#2a2a2a]" />
                      </div>
                    );
                  }
                  return (
                    <MessageItem
                      key={item.msg.id}
                      msg={item.msg}
                      userId={user?.id}
                      token={token}
                      onReply={setReplyTo}
                      onDelete={handleDeleteMessage}
                      onCreateTask={msg => setTaskPrefill(msg.content?.slice(0, 80))}
                    />
                  );
                })
              )}
              {typingUsers.length > 0 && (
                <div className="px-4 py-1 text-xs text-[#555] italic">
                  {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <MessageComposer
              channelId={selectedChannelId}
              projectId={projectId}
              token={token}
              onSent={() => fetchMessages(true)}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              projectUsers={projectUsers}
            />
          </div>

          {/* Task panel - hidden on mobile unless toggled */}
          <div className={`${showTasks ? 'flex' : 'hidden'} md:flex w-full md:w-72 shrink-0 flex-col absolute md:relative inset-0 md:inset-auto bg-[#0d0d0d] z-30 md:z-auto`}>
            {showTasks && (
              <div className="md:hidden flex items-center justify-between px-4 py-2 border-b border-[#2a2a2a] bg-[#1a1a1a]">
                <span className="text-sm font-semibold text-[#f0f0f0]">Tasks</span>
                <button onClick={() => setShowTasks(false)} className="text-[#555] hover:text-[#f0f0f0]">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <TaskPanel
              projectId={projectId}
              channelId={selectedChannelId}
              token={token}
              projectUsers={projectUsers}
              prefill={taskPrefill}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-[#444]">
          <div className="text-center">
            <button
              className="md:hidden mb-4 flex items-center gap-2 mx-auto text-sm text-[#555] hover:text-[#f0f0f0]"
              onClick={() => setShowMobileSidebar(true)}
            >
              <Menu className="h-4 w-4" /> Open Channels
            </button>
            <p>Select a channel</p>
          </div>
        </div>
      )}

      {showSearch && (
        <SearchModal projectId={projectId} token={token} channels={channels}
          onClose={() => setShowSearch(false)}
          onJumpTo={chId => { setSelectedChannelId(chId); setShowSearch(false); }} />
      )}
      {showCreateChannel && (
        <CreateChannelModal projectId={projectId} token={token} projectUsers={projectUsers}
          onCreated={ch => setChannels(prev => [...prev, ch])}
          onClose={() => setShowCreateChannel(false)} />
      )}
      {showNewDM && (
        <NewDMModal
          projectId={projectId}
          token={token}
          projectUsers={projectUsers}
          currentUserId={user?.id}
          onDMCreated={handleDMCreated}
          onClose={() => setShowNewDM(false)}
        />
      )}
    </div>
  );
}
